import { useEffect, useState } from 'react';
import { getActiveLocations } from '@/api/admin/location';
import {
	ImportTypeLocationState,
	ImportTypeLocationStateSchema,
	update,
} from '@/api/admin/location-state';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { ComboboxSelect } from '@/components/ui/custom/combobox-select';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { states } from '@/constants/states';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarIcon, Edit } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function UpdateLocationState({
	locationState: locationState,
}: {
	locationState: ImportTypeLocationState;
}) {
	const [open, setOpen] = useState(false);
	const queryClient = useQueryClient();

	const { data: activeLocations, isLoading: isLoadingActiveLocations } =
		useQuery({
			queryKey: [`active-locations`],
			queryFn: () => getActiveLocations(),
			staleTime: 5 * 60 * 1000, // Cache for 5 minutes
		});

	const { mutate } = useMutation({
		mutationFn: (updatedLocationState: ImportTypeLocationState) =>
			update(updatedLocationState),
		onSuccess: () => {
			toast.success(`Success! Location State has been successfully updated.`, {
				dismissible: true,
			});
			setOpen(false);
			queryClient.invalidateQueries({
				queryKey: [`locationStates-${locationState.importTypeId}`],
			});
		},
		onError: () => {
			toast.error(`Error! Location State could not be updated.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<ImportTypeLocationState>({
		resolver: zodResolver(ImportTypeLocationStateSchema),
		defaultValues: locationState,
		mode: 'all',
	});

	const { reset } = form;
	useEffect(() => {
		reset(locationState);
	}, [locationState, reset]);

	const onSubmit: SubmitHandler<ImportTypeLocationState> = (
		values: ImportTypeLocationState,
	) => mutate(values);

	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" variant="ghost" onClick={() => setOpen(true)}>
							<Edit size={16} />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Update Location State</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent>
					<SheetHeader>
						<SheetTitle>Update Location State</SheetTitle>
						<SheetDescription></SheetDescription>
					</SheetHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="space-y-2">
								<FormField
									control={form.control}
									name="importTypeId"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Import Type ID</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} disabled={true} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="compLocId"
									render={({ field }) => (
										<ComboboxSelect
											field={field}
											options={activeLocations}
											valueKey="compLocId"
											displayKey="brandName"
											isLoading={isLoadingActiveLocations}
											label="Company Location"
											placeholder="Select company location..."
											searchPlaceholder="Search company locations..."
											required={true}
											emptyMessage="No company location found."
											loadingMessage="Loading company locations..."
										/>
									)}
								/>
								<FormField
									control={form.control}
									name="stateId"
									render={({ field }) => (
										<FormItem>
											<FormLabel>State</FormLabel>
											<FormControl>
												<Select
													value={field.value ?? 'none'}
													onValueChange={(val) =>
														field.onChange(val === 'none' ? null : val)
													}
												>
													<SelectTrigger>
														<SelectValue placeholder="Select a state..." />
													</SelectTrigger>
													<SelectContent>
														<SelectGroup>
															<SelectLabel>States</SelectLabel>
															{states!.map((state) => (
																<SelectItem key={state} value={state}>
																	{state}
																</SelectItem>
															))}
														</SelectGroup>
													</SelectContent>
												</Select>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="beginDate"
									render={({ field }) => (
										<FormItem className="flex flex-col">
											<FormLabel>Begin Date</FormLabel>

											<Popover>
												<PopoverTrigger asChild>
													<FormControl>
														<Button
															variant={'outline'}
															className={`w-full pl-3 text-left font-normal ${
																!field.value && 'text-muted-foreground'
															}`}
														>
															{field.value ? (
																new Date(field.value).toLocaleDateString()
															) : (
																<span>Pick a date</span>
															)}
															<CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
														</Button>
													</FormControl>
												</PopoverTrigger>

												<PopoverContent className="w-auto p-0">
													<Calendar
														mode="single"
														selected={field.value ? new Date(field.value) : undefined}
														onSelect={(date) => field.onChange(date)}
														captionLayout="dropdown"
														endMonth={new Date(new Date().getFullYear() + 2, 11)}
													/>
												</PopoverContent>
											</Popover>

											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="expired"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
											<div className="space-y-0.5">
												<FormLabel>Expired</FormLabel>
											</div>
											<FormControl>
												<Switch checked={!!field.value} onCheckedChange={field.onChange} />
											</FormControl>
										</FormItem>
									)}
								/>
							</div>

							<div className="mt-9 flex justify-end">
								<Button
									type="submit"
									disabled={!form.formState.isValid || form.formState.isSubmitting}
								>
									{form.formState.isSubmitting ? 'Updating...' : 'Update'}
								</Button>
							</div>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</div>
	);
}
